import {
	Alert,
	AlertTitle,
	Checkbox,
	FormControlLabel,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";

import { useDateFnsLocale } from "../../../utils/date-locale";
import { RepeatHolidayPreview } from "../controller/use-repeat-holiday-preview";
import { dateKeyToLocalDate } from "../utils/holiday-dates";

interface RepeatShiftHolidayAlertProps {
	// From useRepeatHolidayPreview; the form also uses it to block posting
	// when skipping would leave nothing to create
	preview: RepeatHolidayPreview | null;
	isSkipping: boolean;
	setFieldValue: (field: string, value: unknown) => void;
}

/**
 * Preview of which repeats land on holidays, with the option to leave them
 * out. The count is a preview only — the Repeat Shift response's
 * `skippedHolidayDates` is what was actually excluded.
 */
const RepeatShiftHolidayAlert = ({
	preview,
	isSkipping,
	setFieldValue,
}: RepeatShiftHolidayAlertProps) => {
	const { t } = useTranslation();
	const dateLocale = useDateFnsLocale();

	if (!preview || preview.holidayRepeats.length === 0) return null;

	const { total, holidayRepeats } = preview;
	const count = holidayRepeats.length;

	return (
		<Alert severity="warning" sx={{ mb: 2.5 }}>
			<AlertTitle>
				{t("holidays_repeat-warning-title", { count, total })}
			</AlertTitle>
			<Typography sx={{ fontSize: "14px" }}>
				{holidayRepeats
					.map(
						(repeat) =>
							`${format(dateKeyToLocalDate(repeat.dateKey), "MMM d", {
								locale: dateLocale,
							})} · ${repeat.holidays.map((holiday) => holiday.name).join(", ")}`
					)
					.join(" — ")}
			</Typography>
			<Typography sx={{ fontSize: "14px", fontWeight: 500, mt: 1 }}>
				{preview.isNothingToCreate
					? t("holidays_repeat-outcome-none")
					: isSkipping
					? t("holidays_repeat-outcome-skip", {
						count,
						total,
						remaining: total - count,
					})
					: t("holidays_repeat-outcome-keep", { count, total })}
			</Typography>
			<FormControlLabel
				sx={{ mt: 0.5, "& .MuiFormControlLabel-label": { fontSize: "14px" } }}
				control={
					<Checkbox
						size="small"
						checked={isSkipping}
						onChange={(e) => setFieldValue("skipHolidayDates", e.target.checked)}
					/>
				}
				label={t("holidays_repeat-skip-label")}
			/>
		</Alert>
	);
};

export default RepeatShiftHolidayAlert;
